import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { trackEvent } from '../../../../lib/analytics';

import {
    toggleModalDiagramRename,
    toggleModalDiagramClone,
    toggleModalDiagramDelete,
    toggleModalDiagramShare,
    toggleDiagramDetails,
    diagramPinToggle,
    diagramSoftDelete,
    diagramRestore,
} from '../../../../redux/main/main.actions';
import { selectWorkspacePlan } from '../../../../redux/workspace/workspace.selectors';
import { showNotify } from '../../../../redux/notify/notify.actions';
import './DiagramGrid.scss';
import { timeAgo } from '../../../../utils/time-ago';

import { copyToClipboard } from '../../../../utils/copy-to-clipboard';

function getDbName(db = '') {
    switch (db) {
        case 'basicsql':
            return 'Basic SQL';
        case 'mysql':
            return 'MySQL';
        case 'postgres':
        case 'postgresql':
            return 'PostgreSQL';
        case 'sqlserver':
            return 'SQL Server';
        case 'oracle':
            return 'Oracle';
        case 'sqlite':
            return 'SQLite';
        case 'snowflake':
            return 'Snowflake';
        case 'mariadb':
            return 'MariaDB';
        default:
            return 'Basic SQL';
    }
}

function DiagramGrid({ data: { id, title, description, db_engine, access_role, archived_at, updated_at, last_edited_at, is_pinned, pinned_at, is_example = false } }) {
    const { t } = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();

    const ref = useRef(null);
    const [isOptionsHovered, setIsOptionsHovered] = useState(false);
    const [isOptionsClicked, setIsOptionsClicked] = useState(false);

    const plan = useSelector(selectWorkspacePlan);
    const isFreePlan = plan === 'free';
    const canEdit = ['edit', 'admin', 'owner'].includes(access_role) || isFreePlan;
    const canManage = ['admin', 'owner'].includes(access_role) || isFreePlan;
    const canDelete = ['admin', 'owner'].includes(access_role);
    const canArchive = ['admin', 'owner'].includes(access_role);
    const showOptionsButton = !archived_at || canManage;

    useEffect(() => {
        const handleOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setIsOptionsHovered(false);
                setIsOptionsClicked(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, []);


    const handleMenuItemClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOptionsClicked(false);
    };


    const handleCloneDiagramClick = (e) => {
        trackEvent('app_diagrams_menu_duplicate', {
            page: 'app',
            type: 'diagrams',
            subtype: 'menu',
        });
        dispatch(toggleModalDiagramClone('main', id));
        handleMenuItemClick(e);
    };

    const handleRenameDiagramClick = (e) => {
        trackEvent('app_diagrams_menu_rename', {
            page: 'app',
            type: 'diagrams',
            subtype: 'menu',
        });
        dispatch(toggleModalDiagramRename(id));
        handleMenuItemClick(e);
    };

    const handleSoftDeleteDiagramClick = (e) => {
        trackEvent('app_diagrams_menu_soft_delete', {
            page: 'app',
            type: 'diagrams',
            subtype: 'menu',
        });
        dispatch(diagramSoftDelete(id));
        handleMenuItemClick(e);
    };

    const handleDeleteDiagramClick = (e) => {
        trackEvent('app_diagrams_menu_delete', {
            page: 'app',
            type: 'diagrams',
            subtype: 'menu',
        });
        dispatch(toggleModalDiagramDelete(id));
        handleMenuItemClick(e);
    };

    const handleRestoreDiagramClick = (e) => {
        trackEvent('app_diagrams_menu_restore', {
            page: 'app',
            type: 'diagrams',
            subtype: 'menu',
        });
        dispatch(diagramRestore(id));
        handleMenuItemClick(e);
    };

    const handleShareDiagramClick = (e) => {
        trackEvent('app_diagrams_menu_share', {
            page: 'app',
            type: 'diagrams',
            subtype: 'menu',
        });
        dispatch(toggleModalDiagramShare('main', id));
        handleMenuItemClick(e);
    };

    const handleDiagramDetailsClick = (e) => {
        trackEvent('app_diagrams_menu_details', {
            page: 'app',
            type: 'diagrams',
            subtype: 'menu',
        });
        dispatch(toggleDiagramDetails('main', id));
        handleMenuItemClick(e);
    };

    const handlePinToggleClick = (e) => {
        const nextPinned = !is_pinned;
        trackEvent(nextPinned ? 'app_diagrams_menu_pin' : 'app_diagrams_menu_unpin', {
            page: 'app',
            type: 'diagrams',
            subtype: 'menu',
        });
        dispatch(diagramPinToggle({
            id,
            nextPinned,
            prevPinned: is_pinned,
            prevPinnedAt: pinned_at,
        }));
        handleMenuItemClick(e);
    };

    // const handleCopyDiagramLinkClick = async (e) => {
    //     const baseUrl = window.location.origin;
    //     const path = `/d/${id}`;
    //     const fullUrl = `${baseUrl}${path}`;

    //     try {
    //         await navigator.clipboard.writeText(fullUrl);
    //         dispatch(showNotify({ message: 'link_copied' }));
    //     } catch (err) {
    //         // console.error("error:", err);
    //     }

    //     handleMenuItemClick(e);
    // };
    const handleCopyDiagramLinkClick = async (e) => {
        trackEvent('app_diagrams_menu_copy_link', {
            page: 'app',
            type: 'diagrams',
            subtype: 'menu',
        });

        const baseUrl = window.location.origin;
        const path = `/d/${id}`;
        const fullUrl = `${baseUrl}${path}`;

        e?.preventDefault?.();
        const ok = await copyToClipboard(fullUrl);
        if (ok) {
            dispatch(showNotify({ message: 'link_copied' }));
        } else {
            // dispatch(showNotify({ message: 'code_copy_failed' }));
        }

        handleMenuItemClick(e);
    };

    const handleMenuClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleOpenDiagram = (e) => {
        trackEvent('app_diagrams_click_diagram', {
            page: 'app',
            type: 'diagrams',
        });
        e.preventDefault();
        history.push(`/d/${id}`);
    };


    return (
        <a
            // ref={ref}
            className={`diagram diagram-grid ${isOptionsClicked ? 'menu-open' : ''}`}
            href={`/d/${id}`}
            onMouseEnter={() => { setIsOptionsHovered(true); }}
            onMouseLeave={() => {
                if (isOptionsClicked) return;
                setIsOptionsHovered(false);
            }}
            onClick={(e) => { handleOpenDiagram(e); }}
        >

            <div className="diagram-labels">
                <div className={`diagram-db-engine ${db_engine}`}>{getDbName(db_engine)}</div>
                {/* {is_pinned ? <div className="diagram-db-engine mark-star"><svg className="icon-mark-star-fill" fill="#000000" width="18px" height="18px" viewBox="0 1 24 24"><g><path d="M22,10.1c0.1-0.5-0.3-1.1-0.8-1.1l-5.7-0.8L12.9,3c-0.1-0.2-0.2-0.3-0.4-0.4C12,2.3,11.4,2.5,11.1,3L8.6,8.2L2.9,9C2.6,9,2.4,9.1,2.3,9.3c-0.4,0.4-0.4,1,0,1.4l4.1,4l-1,5.7c0,0.2,0,0.4,0.1,0.6c0.3,0.5,0.9,0.7,1.4,0.4l5.1-2.7l5.1,2.7c0.1,0.1,0.3,0.1,0.5,0.1l0,0c0.1,0,0.1,0,0.2,0c0.5-0.1,0.9-0.6,0.8-1.2l-1-5.7l4.1-4C21.9,10.5,22,10.3,22,10.1z" strokeWidth="0" fill="rgb(229, 234, 239)" stroke="rgb(229, 234, 239)"></path></g></svg></div> : ''} */}
                {is_pinned ? <div className="diagram-db-engine mark-star"><svg width="64px" height="64px" viewBox="0 1 24 24" fill="none" style={{ width: '17px', height: '21px', marginLeft: '3px', marginRight: '-5px', marginTop: '0px', color: 'rgb(172, 173, 175)' }}><path d="M8.37658 15.6162L2.71973 21.273M11.6943 6.64169L10.1334 8.20258C10.0061 8.3299 9.9424 8.39357 9.86986 8.44415C9.80548 8.48905 9.73604 8.52622 9.66297 8.55488C9.58065 8.58717 9.49236 8.60482 9.3158 8.64014L5.65133 9.37303C4.69903 9.56349 4.22288 9.65872 4.00012 9.90977C3.80605 10.1285 3.71743 10.4212 3.75758 10.7108C3.80367 11.0433 4.14703 11.3866 4.83375 12.0733L11.9195 19.1591C12.6062 19.8458 12.9496 20.1891 13.282 20.2352C13.5716 20.2754 13.8643 20.1868 14.083 19.9927C14.3341 19.7699 14.4293 19.2938 14.6198 18.3415L15.3527 14.677C15.388 14.5005 15.4056 14.4122 15.4379 14.3298C15.4666 14.2568 15.5038 14.1873 15.5487 14.123C15.5992 14.0504 15.6629 13.9868 15.7902 13.8594L17.3511 12.2985C17.4325 12.2171 17.4732 12.1764 17.518 12.1409C17.5577 12.1093 17.5998 12.0808 17.6439 12.0557C17.6935 12.0273 17.7464 12.0046 17.8522 11.9593L20.3466 10.8903C21.0743 10.5784 21.4381 10.4225 21.6034 10.1705C21.7479 9.95013 21.7996 9.68163 21.7473 9.42335C21.6874 9.12801 21.4075 8.8481 20.8477 8.28827L15.7045 3.14514C15.1447 2.58531 14.8648 2.3054 14.5695 2.24552C14.3112 2.19317 14.0427 2.24488 13.8223 2.38941C13.5703 2.55469 13.4144 2.91854 13.1025 3.64624L12.0335 6.14059C11.9882 6.24641 11.9655 6.29932 11.9372 6.34893C11.912 6.393 11.8835 6.4351 11.8519 6.47484C11.8164 6.51958 11.7757 6.56029 11.6943 6.64169Z" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" strokeWidth="1.8px"></path></svg></div> : ''}
                {access_role !== 'owner' ? <div className="diagram-db-engine shared">{t('dashboard.filter_shared')}</div> : ''}
                {archived_at ? <div className="diagram-db-engine archived">{t('dashboard.filter_archived')}</div> : ''}
                {is_example && (!archived_at && access_role === 'owner') ? <div className="diagram-db-engine archived">{t('dashboard.example_diagram_badge')}</div> : ''}

                {/* {teams && teams?.name ? <div className="diagram-db-engine archived">{team?.name}</div> : ''} */}
                {/* {teams ? <div className="diagram-db-engine archived">/ Team X /</div> : ''} */}
            </div>


            <div className='diagram-title'>{title}</div>
            {/* <div className='diagram-description'>{description}</div> */}

            <div className="diagram-meta-data-wrap">
                {/* <div className='diagram-meta-data-group'>
                    <div className="diagram-meta-data-item">
                        <svg className="icon-table-group-new" width="30px" height="32px" viewBox="0 0 256 256" fill="none" strokeWidth="10" stroke="#ffffff"><g> <path d="M148,40a4.0002,4.0002,0,0,1-4,4H112a4,4,0,0,1,0-8h32A4.0002,4.0002,0,0,1,148,40Zm-4,172H112a4,4,0,0,0,0,8h32a4,4,0,0,0,0-8ZM208,36H184a4,4,0,0,0,0,8h24a4.00458,4.00458,0,0,1,4,4V71.99951a4,4,0,0,0,8,0V48A12.01375,12.01375,0,0,0,208,36Zm8,71.99951a4.0002,4.0002,0,0,0-4,4v32a4,4,0,0,0,8,0v-32A4.0002,4.0002,0,0,0,216,107.99951Zm0,72a4.0002,4.0002,0,0,0-4,4V208a4.00458,4.00458,0,0,1-4,4H184a4,4,0,0,0,0,8h24a12.01375,12.01375,0,0,0,12-12V183.99951A4.0002,4.0002,0,0,0,216,179.99951Zm-176-32a4.0002,4.0002,0,0,0,4-4v-32a4,4,0,1,0-8,0v32A4.0002,4.0002,0,0,0,40,147.99951ZM72,212H48a4.00458,4.00458,0,0,1-4-4V183.99951a4,4,0,0,0-8,0V208a12.01375,12.01375,0,0,0,12,12H72a4,4,0,0,0,0-8ZM72,36H48A12.01375,12.01375,0,0,0,36,48V71.99951a4,4,0,0,0,8,0V48a4.00458,4.00458,0,0,1,4-4H72a4,4,0,0,0,0-8Z"></path> </g></svg>
                        <span>2</span>
                    </div>
                    <div className="diagram-meta-data-item">
                        <svg className="icon-table-box" width="200px" height="200px" viewBox="0 0.5 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_iconCarrier"> <path d="M4 12L20 12M12 4L12 20M6.2 20H17.8C18.9201 20 19.4802 20 19.908 19.782C20.2843 19.5903 20.5903 19.2843 20.782 18.908C21 18.4802 21 17.9201 21 16.8V7.2C21 6.0799 21 5.51984 20.782 5.09202C20.5903 4.71569 20.2843 4.40973 19.908 4.21799C19.4802 4 18.9201 4 17.8 4H6.2C5.0799 4 4.51984 4 4.09202 4.21799C3.71569 4.40973 3.40973 4.71569 3.21799 5.09202C3 5.51984 3 6.07989 3 7.2V16.8C3 17.9201 3 18.4802 3.21799 18.908C3.40973 19.2843 3.71569 19.5903 4.09202 19.782C4.51984 20 5.07989 20 6.2 20Z" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path> </g></svg>
                        <span>14</span>
                    </div> */}
                {/* <div className="diagram-meta-data-item">
                        <svg className="icon-note" width="28px" height="30px" viewBox="-1.5 -0.5 33 25" strokeWidth="1.1"><g><path d="M 11.5 12 M 27.5 6 V 19.5858 a 1.4903 1.4903 0 0 1 -0.4394 1.0607 l -6.4141 6.4142 A 1.4902 1.4902 0 0 1 19.5858 27.5 H 6 a 1.5017 1.5017 0 0 1 -1.5 -1.5 V 1 A 1.5017 1.5017 0 0 1 6 -0.5 H 26 A 1.5017 1.5017 0 0 1 27.5 1 Z M 6 26.5 H 19.5 V 20 a 0.5 0.5 0 0 1 0.5 -0.5 h 6.5 V 1 a 0.5006 0.5006 0 0 0 -0.5 -0.5 H 6 a 0.5006 0.5006 0 0 0 -0.5 0.5 V 26 A 0.5006 0.5006 0 0 0 6 26.5 Z m 19.7939 -6.001 H 20.5 v 5.2938 Z"></path></g></svg>
                        <span>3</span>
                    </div> */}
                {/* </div> */}
                {/* <div className="diagram-edit-date">{timeAgo(updated_at)}</div> */}
                <div className="diagram-edit-date">{timeAgo(last_edited_at)}</div>
            </div>

            {isOptionsHovered && showOptionsButton ?
                <button
                    type="button"
                    className={`element-button options visible ${isOptionsClicked ? 'clicked' : ''}`}
                    onClick={(e) => {
                        trackEvent('app_diagrams_menu_click', {
                            page: 'app',
                            type: 'diagrams',
                            subtype: 'menu',
                        });

                        e.preventDefault();
                        e.stopPropagation();
                        setIsOptionsClicked((state) => !state);
                    }}
                >
                    <svg height="28px" width="28px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 488" fill="#000000">
                        <g>
                            <circle cx="51.2" cy="244" r="50"></circle>
                            <circle cx="244" cy="244" r="50"></circle>
                            <circle cx="436.8" cy="244" r="50"></circle>
                        </g>
                    </svg>
                </button>
                : ''
            }
            {isOptionsClicked && ReactDOM.createPortal(
                <div
                    className="diagram-grid-menu-overlay"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsOptionsClicked(false);
                        setIsOptionsHovered(false);
                    }}
                ></div>,
                document.body
            )}
            {isOptionsClicked && (
                <div className={`profile-menu ${archived_at ? 'archived' : ''}`} onClick={(e) => { handleMenuClick(e); }} >
                    {
                        !archived_at ?
                            <React.Fragment>
                                <div className="control">
                                    <button
                                        className={`control-button`}
                                        type="button"
                                        title=""
                                        aria-label="Color"
                                        aria-pressed="false"
                                        onClick={(e) => { handleDiagramDetailsClick(e); }}
                                    >
                                        <svg style={{ marginRight: '-2px' }} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"></path></svg>
                                        {/* <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', margin: '1px -3px 0px 1px', strokeWidth: '2.2px !important' }}><path d="M2.5 21.4998L8.04927 19.3655C8.40421 19.229 8.58168 19.1607 8.74772 19.0716C8.8952 18.9924 9.0358 18.901 9.16804 18.7984C9.31692 18.6829 9.45137 18.5484 9.72028 18.2795L21 6.99982C22.1046 5.89525 22.1046 4.10438 21 2.99981C19.8955 1.89525 18.1046 1.89524 17 2.99981L5.72028 14.2795C5.45138 14.5484 5.31692 14.6829 5.20139 14.8318C5.09877 14.964 5.0074 15.1046 4.92823 15.2521C4.83911 15.4181 4.77085 15.5956 4.63433 15.9506L2.5 21.4998ZM2.5 21.4998L4.55812 16.1488C4.7054 15.7659 4.77903 15.5744 4.90534 15.4867C5.01572 15.4101 5.1523 15.3811 5.2843 15.4063C5.43533 15.4351 5.58038 15.5802 5.87048 15.8703L8.12957 18.1294C8.41967 18.4195 8.56472 18.5645 8.59356 18.7155C8.61877 18.8475 8.58979 18.9841 8.51314 19.0945C8.42545 19.2208 8.23399 19.2944 7.85107 19.4417L2.5 21.4998Z"></path></svg> */}
                                        {t('sidebar.see_details')}
                                    </button>
                                </div>

                                {/* <div className="horizontal-line"></div> */}

                                <div className="control" style={{ marginTop: '-1px' }}>
                                {/* <div className="control"> */}
                                    <button
                                        className={`control-button t_btn_open_new_tab`}
                                        type="button"
                                        title=""
                                        aria-label="Color"
                                        aria-pressed="false"
                                        onClick={(e) => {
                                            trackEvent('app_diagrams_menu_new_tab', {
                                                page: 'app',
                                                type: 'diagrams',
                                                subtype: 'menu',
                                            });
                                            handleMenuItemClick(e);
                                            window.open(`/d/${id}`, '_blank');
                                        }}
                                    >
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 9.00001L21 3.00001M21 3.00001H15M21 3.00001L12 12M10 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V14"></path></svg>
                                        {t('sidebar.open_new_tab')}
                                    </button>
                                </div>

                                {/* {canEdit && <div className="horizontal-line"></div>} */}
                                <div className="horizontal-line"></div>

                                <div className="control" style={{ marginBottom: '-1px' }}>
                                    <button
                                        className={`control-button`}
                                        type="button"
                                        title=""
                                        // aria-label={is_pinned ? 'Unmark as Starred' : 'Mark as Starred'}
                                        aria-pressed={is_pinned ? 'true' : 'false'}
                                        onClick={(e) => { handlePinToggleClick(e); }}
                                    >
                                        {/* <svg className="icon-mark-star" fill="#ffffff" width="18px" height="18px" viewBox="0 0 24 24" stroke="#ffffff"><g><path d="M22,9.67A1,1,0,0,0,21.14,9l-5.69-.83L12.9,3a1,1,0,0,0-1.8,0L8.55,8.16,2.86,9a1,1,0,0,0-.81.68,1,1,0,0,0,.25,1l4.13,4-1,5.68A1,1,0,0,0,6.9,21.44L12,18.77l5.1,2.67a.93.93,0,0,0,.46.12,1,1,0,0,0,.59-.19,1,1,0,0,0,.4-1l-1-5.68,4.13-4A1,1,0,0,0,22,9.67Zm-6.15,4a1,1,0,0,0-.29.88l.72,4.2-3.76-2a1.06,1.06,0,0,0-.94,0l-3.76,2,.72-4.2a1,1,0,0,0-.29-.88l-3-3,4.21-.61a1,1,0,0,0,.76-.55L12,5.7l1.88,3.82a1,1,0,0,0,.76.55l4.21.61Z" strokeWidth="0.1px" fill="rgb(227, 233, 240)" stroke="rgb(227, 233, 240)"></path></g></svg> */}
                                        {/* {is_pinned ? 'Unmark as starred' : 'Mark as starred'} */}
                                        {/* {is_pinned ? 'Unstar' : 'Star'} */}
                                        {/* {is_pinned ? 'Unpin' : 'Pin'} */}
                                        {is_pinned
                                            ? <svg width="64px" height="64px" viewBox="0 0 24 24" fill="none" style={{ width: '16px', marginRight: '-1px', height: '20px' }}>
                                                <path d="M17.1218 1.87023C15.7573 0.505682 13.4779 0.76575 12.4558 2.40261L9.75191 6.73289L11.1969 8.17793C11.2355 8.1273 11.2723 8.07415 11.3071 8.01845L14.1523 3.46191C14.493 2.91629 15.2528 2.8296 15.7076 3.28445L20.6359 8.21274C21.0907 8.66759 21.0041 9.42737 20.4584 9.76806L15.9019 12.6133C15.8462 12.6481 15.793 12.6848 15.7424 12.7234L17.1874 14.1684L21.5177 11.4645C23.1546 10.4424 23.4147 8.16307 22.0501 6.79852L17.1218 1.87023Z" fill="currentColor"></path>
                                                <path d="M3.56525 8.85242C3.6015 8.26612 3.84962 7.68582 4.32883 7.27422L5.77735 8.72274C5.75784 8.72967 5.73835 8.7368 5.71886 8.74414C5.64516 8.7719 5.61855 8.80285 5.60548 8.82181C5.58877 8.84604 5.56651 8.8937 5.56144 8.97583C5.55046 9.15333 5.62872 9.40686 5.82846 9.6066L14.3137 18.0919C14.5135 18.2916 14.767 18.3699 14.9445 18.3589C15.0266 18.3538 15.0743 18.3316 15.0985 18.3149C15.1175 18.3018 15.1484 18.2752 15.1762 18.2015C15.1835 18.182 15.1907 18.1625 15.1976 18.143L16.6461 19.5915C16.2345 20.0707 15.6542 20.3188 15.0679 20.3551C14.2853 20.4035 13.4808 20.0874 12.8995 19.5061L9.36397 15.9705L2.68394 22.6506C2.29342 23.0411 1.66025 23.0411 1.26973 22.6506C0.879206 22.26 0.879206 21.6269 1.26973 21.2363L7.94975 14.5563L4.41425 11.0208C3.83293 10.4395 3.51687 9.63502 3.56525 8.85242Z" fill="currentColor"></path>
                                                <path d="M2.00789 2.00786C1.61736 2.39838 1.61736 3.03155 2.00789 3.42207L20.5862 22.0004C20.9767 22.3909 21.6099 22.3909 22.0004 22.0004C22.391 21.6099 22.391 20.9767 22.0004 20.5862L3.4221 2.00786C3.03158 1.61733 2.39841 1.61733 2.00789 2.00786Z" fill="currentColor"></path>
                                            </svg>
                                            : <svg width="64px" height="64px" viewBox="0 0 24 24" fill="none" style={{ width: '17px', marginRight: '-1px', height: '21px' }}><path d="M8.37658 15.6162L2.71973 21.273M11.6943 6.64169L10.1334 8.20258C10.0061 8.3299 9.9424 8.39357 9.86986 8.44415C9.80548 8.48905 9.73604 8.52622 9.66297 8.55488C9.58065 8.58717 9.49236 8.60482 9.3158 8.64014L5.65133 9.37303C4.69903 9.56349 4.22288 9.65872 4.00012 9.90977C3.80605 10.1285 3.71743 10.4212 3.75758 10.7108C3.80367 11.0433 4.14703 11.3866 4.83375 12.0733L11.9195 19.1591C12.6062 19.8458 12.9496 20.1891 13.282 20.2352C13.5716 20.2754 13.8643 20.1868 14.083 19.9927C14.3341 19.7699 14.4293 19.2938 14.6198 18.3415L15.3527 14.677C15.388 14.5005 15.4056 14.4122 15.4379 14.3298C15.4666 14.2568 15.5038 14.1873 15.5487 14.123C15.5992 14.0504 15.6629 13.9868 15.7902 13.8594L17.3511 12.2985C17.4325 12.2171 17.4732 12.1764 17.518 12.1409C17.5577 12.1093 17.5998 12.0808 17.6439 12.0557C17.6935 12.0273 17.7464 12.0046 17.8522 11.9593L20.3466 10.8903C21.0743 10.5784 21.4381 10.4225 21.6034 10.1705C21.7479 9.95013 21.7996 9.68163 21.7473 9.42335C21.6874 9.12801 21.4075 8.8481 20.8477 8.28827L15.7045 3.14514C15.1447 2.58531 14.8648 2.3054 14.5695 2.24552C14.3112 2.19317 14.0427 2.24488 13.8223 2.38941C13.5703 2.55469 13.4144 2.91854 13.1025 3.64624L12.0335 6.14059C11.9882 6.24641 11.9655 6.29932 11.9372 6.34893C11.912 6.393 11.8835 6.4351 11.8519 6.47484C11.8164 6.51958 11.7757 6.56029 11.6943 6.64169Z" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" strokeWidth="1.8px"></path></svg>
                                        }
                                        {is_pinned ? t('sidebar.unpin') : t('sidebar.pin')}
                                    </button>
                                </div>

                                {canEdit && <div className="control">
                                    <button
                                        className={`control-button`}
                                        type="button"
                                        title=""
                                        aria-label="Color"
                                        aria-pressed="false"
                                        onClick={(e) => { handleRenameDiagramClick(e); }}
                                    >
                                        <svg style={{ marginRight: '-1px' }} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 21.4998L8.04927 19.3655C8.40421 19.229 8.58168 19.1607 8.74772 19.0716C8.8952 18.9924 9.0358 18.901 9.16804 18.7984C9.31692 18.6829 9.45137 18.5484 9.72028 18.2795L21 6.99982C22.1046 5.89525 22.1046 4.10438 21 2.99981C19.8955 1.89525 18.1046 1.89524 17 2.99981L5.72028 14.2795C5.45138 14.5484 5.31692 14.6829 5.20139 14.8318C5.09877 14.964 5.0074 15.1046 4.92823 15.2521C4.83911 15.4181 4.77085 15.5956 4.63433 15.9506L2.5 21.4998ZM2.5 21.4998L4.55812 16.1488C4.7054 15.7659 4.77903 15.5744 4.90534 15.4867C5.01572 15.4101 5.1523 15.3811 5.2843 15.4063C5.43533 15.4351 5.58038 15.5802 5.87048 15.8703L8.12957 18.1294C8.41967 18.4195 8.56472 18.5645 8.59356 18.7155C8.61877 18.8475 8.58979 18.9841 8.51314 19.0945C8.42545 19.2208 8.23399 19.2944 7.85107 19.4417L2.5 21.4998Z"></path></svg>
                                        {t('b.rename')}
                                    </button>
                                </div>}


                                {canEdit && <div className="control" style={{ marginBottom: '-1px' }}>
                                    <button
                                        className={`control-button`}
                                        type="button"
                                        title=""
                                        aria-label="Color"
                                        aria-pressed="false"
                                        onClick={(e) => { handleCloneDiagramClick(e); }}
                                    >
                                        <svg className="icon-duplicate" width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"><path d="M8 8V5.2C8 4.0799 8 3.51984 8.21799 3.09202C8.40973 2.71569 8.71569 2.40973 9.09202 2.21799C9.51984 2 10.0799 2 11.2 2H18.8C19.9201 2 20.4802 2 20.908 2.21799C21.2843 2.40973 21.5903 2.71569 21.782 3.09202C22 3.51984 22 4.0799 22 5.2V12.8C22 13.9201 22 14.4802 21.782 14.908C21.5903 15.2843 21.2843 15.5903 20.908 15.782C20.4802 16 19.9201 16 18.8 16H16M5.2 22H12.8C13.9201 22 14.4802 22 14.908 21.782C15.2843 21.5903 15.5903 21.2843 15.782 20.908C16 20.4802 16 19.9201 16 18.8V11.2C16 10.0799 16 9.51984 15.782 9.09202C15.5903 8.71569 15.2843 8.40973 14.908 8.21799C14.4802 8 13.9201 8 12.8 8H5.2C4.0799 8 3.51984 8 3.09202 8.21799C2.71569 8.40973 2.40973 8.71569 2.21799 9.09202C2 9.51984 2 10.0799 2 11.2V18.8C2 19.9201 2 20.4802 2.21799 20.908C2.40973 21.2843 2.71569 21.5903 3.09202 21.782C3.51984 22 4.07989 22 5.2 22Z"></path></svg>
                                        {t('b.duplicate')}
                                    </button>
                                </div>}

                                {canManage && <div className="control">
                                    <button
                                        className={`control-button`}
                                        type="button"
                                        title=""
                                        aria-label="Color"
                                        aria-pressed="false"
                                        onClick={(e) => { handleShareDiagramClick(e); }}
                                    >
                                        {/* <svg width="21" height="21" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V12M16 7L12 3M12 3L8 7M12 3V15"></path></svg> */}
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M7 11C6.07003 11 5.60504 11 5.22354 11.1022C4.18827 11.3796 3.37962 12.1883 3.10222 13.2235C3 13.605 3 14.07 3 15V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V15C21 14.07 21 13.605 20.8978 13.2235C20.6204 12.1883 19.8117 11.3796 18.7765 11.1022C18.395 11 17.93 11 17 11M16 7L12 3M12 3L8 7M12 3V15"></path></svg>
                                        {t('b.share')}
                                    </button>
                                </div>}

                                {/* <div className="control">
                                    <button
                                        className={`control-button`}
                                        type="button"
                                        title=""
                                        aria-label="Color"
                                        aria-pressed="false"
                                        onClick={(e) => {
                                            handleCopyDiagramLinkClick(e);
                                        }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12.7076 18.3639L11.2933 19.7781C9.34072 21.7308 6.1749 21.7308 4.22228 19.7781C2.26966 17.8255 2.26966 14.6597 4.22228 12.7071L5.63649 11.2929M18.3644 12.7071L19.7786 11.2929C21.7312 9.34024 21.7312 6.17441 19.7786 4.22179C17.826 2.26917 14.6602 2.26917 12.7076 4.22179L11.2933 5.636M8.50045 15.4999L15.5005 8.49994"></path></svg>
                                        Copy link
                                    </button>
                                </div> */}

                                {(canArchive || canDelete) && <div className="horizontal-line"></div>}

                                {canArchive && <div className="control" style={{ marginBottom: '-1px', paddingBottom: '1px' }}>
                                    <button
                                        className={`control-button`}
                                        type="button"
                                        title=""
                                        aria-label="Color"
                                        aria-pressed="false"
                                        onClick={(e) => { handleSoftDeleteDiagramClick(e); }}
                                    >
                                        <svg style={{ marginRight: '0px' }} width="15px" height="23px" viewBox="0 0 24 24" fill="none"><g><path d="M3 12V15.8C3 16.9201 3 17.4802 3.21799 17.908C3.40973 18.2843 3.71569 18.5903 4.09202 18.782C4.51984 19 5.0799 19 6.2 19H17.8C18.9201 19 19.4802 19 19.908 18.782C20.2843 18.5903 20.5903 18.2843 20.782 17.908C21 17.4802 21 16.9201 21 15.8V12M3 12H6.67452C7.16369 12 7.40829 12 7.63846 12.0553C7.84254 12.1043 8.03763 12.1851 8.21657 12.2947C8.4184 12.4184 8.59136 12.5914 8.93726 12.9373L9.06274 13.0627C9.40865 13.4086 9.5816 13.5816 9.78343 13.7053C9.96237 13.8149 10.1575 13.8957 10.3615 13.9447C10.5917 14 10.8363 14 11.3255 14H12.6745C13.1637 14 13.4083 14 13.6385 13.9447C13.8425 13.8957 14.0376 13.8149 14.2166 13.7053C14.4184 13.5816 14.5914 13.4086 14.9373 13.0627L15.0627 12.9373C15.4086 12.5914 15.5816 12.4184 15.7834 12.2947C15.9624 12.1851 16.1575 12.1043 16.3615 12.0553C16.5917 12 16.8363 12 17.3255 12H21M3 12L5.32639 6.83025C5.78752 5.8055 6.0181 5.29312 6.38026 4.91755C6.70041 4.58556 7.09278 4.33186 7.52691 4.17615C8.01802 4 8.57988 4 9.70361 4H14.2964C15.4201 4 15.982 4 16.4731 4.17615C16.9072 4.33186 17.2996 4.58556 17.6197 4.91755C17.9819 5.29312 18.2125 5.8055 18.6736 6.83025L21 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path> </g></svg>
                                        {/* <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3H15M3 6H21M19 6L18.2987 16.5193C18.1935 18.0975 18.1409 18.8867 17.8 19.485C17.4999 20.0118 17.0472 20.4353 16.5017 20.6997C15.882 21 15.0911 21 13.5093 21H10.4907C8.90891 21 8.11803 21 7.49834 20.6997C6.95276 20.4353 6.50009 20.0118 6.19998 19.485C5.85911 18.8867 5.8065 18.0975 5.70129 16.5193L5 6M10 10.5V15.5M14 10.5V15.5"></path></svg> */}
                                        {t('b.archive')}
                                    </button>
                                </div>}

                                {canDelete && <div className="control">
                                    <button
                                        className={`control-button control-button-danger`}
                                        type="button"
                                        title=""
                                        aria-label="Color"
                                        aria-pressed="false"
                                        onClick={(e) => { handleDeleteDiagramClick(e); }}
                                    >
                                        <svg style={{ marginLeft: '1px', marginRight: '-1px' }} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3H15M3 6H21M19 6L18.2987 16.5193C18.1935 18.0975 18.1409 18.8867 17.8 19.485C17.4999 20.0118 17.0472 20.4353 16.5017 20.6997C15.882 21 15.0911 21 13.5093 21H10.4907C8.90891 21 8.11803 21 7.49834 20.6997C6.95276 20.4353 6.50009 20.0118 6.19998 19.485C5.85911 18.8867 5.8065 18.0975 5.70129 16.5193L5 6M10 10.5V15.5M14 10.5V15.5"></path></svg>
                                        {t('b.delete')}
                                    </button>
                                </div>}

                            </React.Fragment>
                            :
                            <React.Fragment>
                                {/* <div className="control">
                                    <button
                                        className={`control-button`}
                                        type="button"
                                        title=""
                                        aria-label="Color"
                                        aria-pressed="false"
                                        onClick={(e) => { handleDiagramDetailsClick(e); }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"></path></svg>
                                        See details
                                    </button>
                                </div>

                                <div className="horizontal-line"></div> */}

                                {canManage && <div className="control" style={{ marginBottom: '-2px', paddingBottom: '1px' }}>
                                    <button
                                        className={`control-button`}
                                        type="button"
                                        title=""
                                        aria-label="Color"
                                        aria-pressed="false"
                                        onClick={(e) => { handleRestoreDiagramClick(e); }}
                                    >
                                        <svg style={{ width: '14px' }} width="21" height="21" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V12M16 7L12 3M12 3L8 7M12 3V15"></path></svg>
                                        {t('b.restore')}
                                    </button>
                                </div>}

                                {/* <div className="horizontal-line"></div> */}

                                {canDelete && <div className="control">
                                    <button
                                        className={`control-button control-button-danger`}
                                        type="button"
                                        title=""
                                        aria-label="Color"
                                        aria-pressed="false"
                                        onClick={(e) => { handleDeleteDiagramClick(e); }}
                                    >
                                        <svg style={{ marginRight: '-1px' }} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3H15M3 6H21M19 6L18.2987 16.5193C18.1935 18.0975 18.1409 18.8867 17.8 19.485C17.4999 20.0118 17.0472 20.4353 16.5017 20.6997C15.882 21 15.0911 21 13.5093 21H10.4907C8.90891 21 8.11803 21 7.49834 20.6997C6.95276 20.4353 6.50009 20.0118 6.19998 19.485C5.85911 18.8867 5.8065 18.0975 5.70129 16.5193L5 6M10 10.5V15.5M14 10.5V15.5"></path></svg>
                                        {t('b.delete')}
                                    </button>
                                </div>}
                            </React.Fragment>

                    }

                </div>





                // <ul className="element-button-options-menu">
                //     <li className='element-button-options-menu-item'>
                //         <button
                //             type="button"
                //             className="element-button-options-menu-item-btn"
                //             onClick={(e) => { handleImportSql(e); }}
                //         >
                //             Import SQL
                //         </button>
                //     </li>
                //     <li className='element-button-options-menu-item'>
                //         <button
                //             type="button"
                //             className="element-button-options-menu-item-btn"
                //             onClick={(e) => { handleImportDbml(e); }}
                //         >
                //             Import DBML
                //         </button>
                //     </li>
                // </ul>
            )}
        </a>
    );
}


export default DiagramGrid;
